import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";
import AIChat from "@/components/AIChat";
import AppStoreBadge from "@/components/AppStoreBadge";
import { BUSINESS } from "@/lib/sleek";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/showroom", label: "Showroom" },
  { to: "/visualizer", label: "AI Visualizer" },
  { to: "/financing", label: "Financing" },
  { to: "/blog", label: "Journal" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Layout() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    setOpen(false);
  }, [pathname]);

  return (
    <div className="min-h-screen bg-[var(--sleek-bg)] text-[var(--sleek-ink)]">
      {/* Top thin bar */}
      <div className="hidden md:block border-b border-zinc-200 text-xs">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-2 flex justify-between items-center text-zinc-600">
          <div className="flex items-center gap-6">
            <a href={BUSINESS.mapsHref} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-zinc-900 transition-colors" data-testid="topbar-directions">
              <MapPin size={12} /> {BUSINESS.address}
            </a>
          </div>
          <div className="flex items-center gap-6">
            <a href={BUSINESS.phoneHref} className="flex items-center gap-2 text-zinc-900 font-semibold text-[13px] hover:text-[#FF001E] transition-colors" data-testid="topbar-phone">
              <Phone size={13} /> {BUSINESS.phone}
            </a>
            <a href={BUSINESS.emailHref} className="flex items-center gap-2 hover:text-zinc-900" data-testid="topbar-email">
              <Mail size={12} /> {BUSINESS.email}
            </a>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-zinc-200/70">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 h-20 flex items-center justify-between">
          <Link to="/" className="font-display flex items-center gap-2.5" data-testid="brand-logo">
            <img
              src="/brand/logo.png"
              alt="Sleek Renovations"
              className="h-12 md:h-14 w-auto select-none"
              draggable="false"
              style={{ filter: "brightness(0) saturate(100%) invert(13%) sepia(97%) saturate(6739%) hue-rotate(347deg) brightness(94%) contrast(114%)" }}
            />
            <div className="flex flex-col leading-none pt-1.5 md:pt-2">
              <div className="text-xl tracking-tight whitespace-nowrap">
                <span className="font-light">SLEEK</span>
                <span className="font-medium ml-1">RENOVATIONS</span>
              </div>
              <div className="hidden md:block text-[10px] tracking-[0.3em] text-zinc-500 uppercase mt-1.5 whitespace-nowrap">
                Kitchen · Bath · Whole-Home
              </div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-7 text-sm">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `relative py-2 transition-colors whitespace-nowrap ${isActive ? "text-zinc-950" : "text-zinc-500 hover:text-zinc-950"}`
                }
              >
                {({ isActive }) => (
                  <>
                    {n.label}
                    {isActive && <span className="absolute left-0 right-0 -bottom-0.5 h-px bg-zinc-950" />}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/booking"
              data-testid="header-cta-booking"
              className="hidden md:inline-flex items-center gap-2 bg-[#FF001E] text-white px-5 py-3 text-sm font-medium hover:bg-[#D60019] transition whitespace-nowrap"
            >
              Book Consultation <ArrowUpRight size={14} />
            </Link>
            <a
              href={BUSINESS.phoneHref}
              aria-label={`Call ${BUSINESS.phone}`}
              data-testid="mobile-call-button"
              className="md:hidden inline-flex items-center justify-center h-10 w-10 border border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white transition-colors"
            >
              <Phone size={17} />
            </a>
            <button
              className="lg:hidden p-2"
              onClick={() => setOpen(!open)}
              aria-label="menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden border-t border-zinc-200 bg-white">
            <div className="px-6 py-4 flex flex-col gap-3">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  className={({ isActive }) =>
                    `py-2 text-base ${isActive ? "text-zinc-950 font-medium" : "text-zinc-600"}`
                  }
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {n.label}
                </NavLink>
              ))}
              <Link to="/booking" className="bg-[#FF001E] text-white px-5 py-3 text-sm font-medium text-center mt-2 hover:bg-[#D60019] transition">
                Book Consultation
              </Link>
              <a
                href={BUSINESS.phoneHref}
                data-testid="mobile-menu-call-link"
                className="flex items-center justify-center gap-2 border border-zinc-900 text-zinc-900 px-5 py-3 text-sm font-semibold hover:bg-zinc-900 hover:text-white transition-colors"
              >
                <Phone size={15} /> Call {BUSINESS.phone}
              </a>
            </div>
          </div>
        )}
      </header>

      <main className="min-h-[60vh]">
        <Outlet />
      </main>

      <Footer />
      <AIChat />
    </div>
  );
}

function Footer() {
  return (
    <footer className="border-t border-zinc-200 bg-white mt-24">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-20">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-3">
            <div className="font-display text-3xl font-light tracking-tight">
              Two brands.<br />One promise of craft.
            </div>
            <p className="mt-6 text-zinc-600 max-w-md leading-relaxed">
              Sleek Renovations designs and builds kitchen, bath, and whole-home remodels across MetroWest Boston.
              Sleek Kitchen &amp; Bath is our adjoining Northborough showroom — touch every finish before you commit.
            </p>
            <Link
              to="/booking"
              className="inline-flex items-center gap-2 mt-8 bg-zinc-950 text-white px-6 py-3 text-sm font-medium hover:bg-zinc-800 transition"
              data-testid="footer-cta-booking"
            >
              Book a free consultation <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="md:col-span-3">
            <div className="text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4">Visit</div>
            <p className="text-zinc-700 leading-relaxed">
              <a href={BUSINESS.mapsHref} target="_blank" rel="noopener noreferrer" className="hover:text-zinc-950 transition-colors" data-testid="footer-directions">
                {BUSINESS.address}
              </a>
            </p>
            <p className="text-zinc-700 mt-4">
              By Appointment Only
            </p>
          </div>
          <div className="md:col-span-3">
            <div className="text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4">Contact</div>
            <a href={BUSINESS.phoneHref} className="block text-zinc-700 hover:text-zinc-950">{BUSINESS.phone}</a>
            <a href={BUSINESS.emailHref} className="block text-zinc-700 hover:text-zinc-950 mt-2 break-words">{BUSINESS.email}</a>
            <div className="mt-6">
              <AppStoreBadge testid="footer-app-store-badge" />
            </div>
          </div>
          <div className="md:col-span-3">
            <div className="text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4">Explore</div>
            <ul className="space-y-2 text-zinc-700">
              <li><Link to="/services" className="hover:text-zinc-950">Services</Link></li>
              <li><Link to="/portfolio" className="hover:text-zinc-950">Portfolio</Link></li>
              <li><Link to="/showroom" className="hover:text-zinc-950">Showroom</Link></li>
              <li><Link to="/visualizer" className="hover:text-zinc-950">AI Visualizer</Link></li>
              <li><Link to="/financing" className="hover:text-zinc-950">Financing</Link></li>
              <li><Link to="/blog" className="hover:text-zinc-950">Journal</Link></li>
              <li><Link to="/support" className="hover:text-zinc-950" data-testid="footer-support-link">Support</Link></li>
            </ul>
          </div>
        </div>
        <div className="mt-16 pt-8 border-t border-zinc-200 flex flex-col md:flex-row justify-between text-xs text-zinc-500">
          <div>© {new Date().getFullYear()} Sleek Renovations · Sleek Kitchen &amp; Bath. Licensed &amp; insured in MA. · <Link to="/privacy" className="hover:text-zinc-950 underline" data-testid="footer-privacy-link">Privacy</Link></div>
          <div className="mt-3 md:mt-0">Designed in Northborough.</div>
        </div>
      </div>
    </footer>
  );
}
